<template>
  <div class="field-toolbar">
    <!-- Signer selector -->
    <div class="toolbar-section q-mb-md">
      <div class="text-caption text-grey-7 q-mb-xs">Assign to</div>
      <div class="signer-buttons">
        <q-btn
          v-for="(signer, index) in signers"
          :key="index"
          dense
          no-caps
          :outline="index !== activeSignerIndex"
          :style="signerBtnStyle(index)"
          class="signer-btn"
          align="left"
          @click="$emit('signer-change', index)"
        >
          <q-icon name="person" size="14px" class="q-mr-xs" />
          <span class="ellipsis">{{ signerDisplayName(signer, index) }}</span>
        </q-btn>
      </div>
    </div>

    <q-separator />

    <!-- Field types -->
    <div class="toolbar-section q-mt-md">
      <div class="text-caption text-grey-7 q-mb-xs">Drag fields onto document</div>
      <div class="field-type-list">
        <div
          v-for="ft in fieldTypes"
          :key="ft.type"
          class="field-type-item"
          draggable="true"
          :style="fieldItemStyle"
          @dragstart="startDrag($event, ft.type)"
        >
          <q-icon :name="ft.icon" size="20px" :color="activeColor.name.toLowerCase()" />
          <span class="q-ml-sm text-body2">{{ ft.label }}</span>
          <q-tooltip v-if="ft.tooltip" anchor="center right" self="center left" :offset="[8, 0]" :delay="400">
            {{ ft.tooltip }}
          </q-tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  FIELD_TYPE_LIST, FIELD_TYPES, getSignerColor, ptToPixels,
  TEXT_FIELD_TYPES, FIELD_FONT_FAMILY, FIELD_LEFT_PADDING_PT,
  FIELD_BASELINE_OFFSET_RATIO, computeFieldFontSize,
  resolveAnnotationDefaults
} from '../pdf/fieldConstants.js';
import { useAnnotationFieldTypesStore } from '../../stores/annotationFieldTypes.js';

export default {
  name: 'FieldToolbar',
  props: {
    signers: { type: Array, default: () => [] },
    activeSignerIndex: { type: Number, default: 0 },
    scale: { type: Number, default: 1 },
    /**
     * A representative page used to size the drag-preview ghost so it
     * matches what the actual dropped field will look like (uses the
     * page's medianLineHeightPt for Layer 2 sizing). Pass the active
     * document's first page; if omitted, the ghost falls back to the
     * page-size fraction default.
     */
    referencePage: { type: Object, default: null },
  },
  data() {
    return { _fieldTypesStore: null };
  },
  created() {
    this._fieldTypesStore = useAnnotationFieldTypesStore();
  },
  emits: ['signer-change'],
  computed: {
    fieldTypes() {
      return FIELD_TYPE_LIST;
    },
    activeColor() {
      const signer = this.signers[this.activeSignerIndex];
      return getSignerColor(signer?.colorIndex ?? this.activeSignerIndex);
    },
    fieldItemStyle() {
      const color = this.activeColor;
      return {
        borderLeft: `3px solid ${color.border}`,
        backgroundColor: color.bg,
      };
    },
  },
  methods: {
    signerDisplayName(signer, index) {
      return signer.name || signer.email || `Signer ${index + 1}`;
    },
    signerBtnStyle(index) {
      const signer = this.signers[index];
      const color = getSignerColor(signer?.colorIndex ?? index);
      if (index === this.activeSignerIndex) {
        return {
          backgroundColor: color.border,
          color: '#fff',
          borderColor: color.border,
        };
      }
      return {
        color: color.text,
        borderColor: color.border,
      };
    },
    startDrag(event, fieldType) {
      event.dataTransfer.setData('application/x-esign-field-type', fieldType);
      event.dataTransfer.effectAllowed = 'copy';

      // Create a custom drag image matching what the dropped field will
      // look like - uses the same resolveAnnotationDefaults math as the
      // drop handler so preview and result agree.
      const fieldDef = FIELD_TYPES[fieldType];
      if (fieldDef && this.scale > 0) {
        const color = this.activeColor;
        const { widthPt: defaultWidthPt, heightPt: defaultHeightPt } = resolveAnnotationDefaults(
          fieldType,
          this.referencePage,
          this._fieldTypesStore?.types || null,
          this._fieldTypesStore?.fallbackLineHeightFraction ?? 0.018
        );
        const w = Math.round(ptToPixels(defaultWidthPt, this.scale));
        const h = Math.round(ptToPixels(defaultHeightPt, this.scale));

        const isText = TEXT_FIELD_TYPES.has(fieldType);
        const fontSizePt = isText
          ? computeFieldFontSize(defaultHeightPt)
          : Math.max(10, Math.min(14, defaultHeightPt * 0.6));
        const fontSizePx = Math.round(ptToPixels(fontSizePt, this.scale));
        const leftPadPx = Math.max(4, Math.round(ptToPixels(FIELD_LEFT_PADDING_PT, this.scale)));
        const bottomPadPx = isText ? Math.round(h * FIELD_BASELINE_OFFSET_RATIO) : 0;

        const ghost = document.createElement('div');
        ghost.style.cssText = `
          width: ${w}px; height: ${h}px;
          background: ${color.bg};
          border: 2px solid ${color.border};
          border-radius: 3px;
          display: flex;
          align-items: ${isText ? 'flex-end' : 'center'};
          justify-content: flex-start;
          padding: 0 ${leftPadPx}px ${bottomPadPx}px ${leftPadPx}px;
          font-size: ${fontSizePx}px;
          font-family: ${FIELD_FONT_FAMILY};
          color: ${color.text};
          position: fixed; top: -9999px; left: -9999px;
          pointer-events: none;
          box-sizing: border-box;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
          line-height: 1;
        `;
        ghost.textContent = fieldDef.label;
        document.body.appendChild(ghost);

        // Center the ghost on the cursor
        event.dataTransfer.setDragImage(ghost, w / 2, h / 2);

        // Clean up after the drag system captures the image
        requestAnimationFrame(() => document.body.removeChild(ghost));
      }
    },
  },
};
</script>

<style scoped>
.field-toolbar {
  padding: 12px;
  width: 200px;
  min-width: 200px;
  overflow-y: auto;
  border-right: 1px solid #e0e0e0;
  background: #fafafa;
}

.signer-buttons {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.signer-btn {
  width: 100%;
}

.field-type-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.field-type-item {
  display: flex;
  align-items: center;
  padding: 8px 10px;
  border-radius: 4px;
  cursor: grab;
  user-select: none;
  transition: box-shadow 0.15s;
}

.field-type-item:hover {
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
}

.field-type-item:active {
  cursor: grabbing;
  opacity: 0.8;
}
</style>
